import { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import api, { formatApiErrorDetail } from "@/lib/api";
import Navbar from "@/components/Navbar";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import AdminMarketing from "@/components/AdminMarketing";
import AdminAnnouncements from "@/components/AdminAnnouncements";
import AdminTickets from "@/components/admin/AdminTickets";
import { StatCard, emptyEdit } from "@/components/admin/shared";
import MembersTab from "@/components/admin/MembersTab";
import AffiliatesTab from "@/components/admin/AffiliatesTab";
import EmailTemplatesTab from "@/components/admin/EmailTemplatesTab";
import NotificationSettings from "@/components/admin/NotificationSettings";
import MemberFormDialog from "@/components/admin/MemberFormDialog";
import EditMemberDialog from "@/components/admin/EditMemberDialog";
import AffiliateDetailDialog from "@/components/admin/AffiliateDetailDialog";
import { Users, Clock, CheckCircle2, Handshake, Ban } from "lucide-react";
import { toast } from "sonner";

const AdminDashboard = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const validTabs = ["members", "affiliates", "emails", "marketing", "announcements", "support"];
  const activeTab = validTabs.includes(searchParams.get("tab")) ? searchParams.get("tab") : "members";
  const setTab = (t) => setSearchParams(t === "members" ? {} : { tab: t });
  const [stats, setStats] = useState(null);
  const [awaitingCount, setAwaitingCount] = useState(0);
  const [members, setMembers] = useState([]);
  const [memberDialog, setMemberDialog] = useState(false);
  const [memberForm, setMemberForm] = useState({ name: "", email: "", phone: "", password: "", generate_code: true, is_affiliate: false });
  const [tpl, setTpl] = useState({ subject: "", body_html: "", email_configured: false, sender_email: "" });
  const [tplKind, setTplKind] = useState("member");
  const [testEmail, setTestEmail] = useState("");
  const [affiliates, setAffiliates] = useState([]);
  const [commission, setCommission] = useState({ percent: 30, membership_fee: 0 });
  const [affDetail, setAffDetail] = useState({ open: false, affiliate: null, referrals: [], payments: [] });
  const [affLinks, setAffLinks] = useState([]);
  const [affNotes, setAffNotes] = useState([]);
  const [newLink, setNewLink] = useState({ name: "", destination: "homepage", commission_percent: "", promo_code: "" });
  const [destinations, setDestinations] = useState([]);
  const [newNote, setNewNote] = useState("");
  const [editingLink, setEditingLink] = useState(null);
  const [paySel, setPaySel] = useState([]);
  const [payMeta, setPayMeta] = useState({ method: "", reference: "" });
  const [edit, setEdit] = useState({ open: false, id: null });
  const [editForm, setEditForm] = useState(emptyEdit);
  const [editOriginal, setEditOriginal] = useState(emptyEdit);
  const [saving, setSaving] = useState(false);
  const [discardOpen, setDiscardOpen] = useState(false);

  const dirty = JSON.stringify(editForm) !== JSON.stringify(editOriginal);

  const loadTemplate = useCallback(async (kind) => {
    const { data } = await api.get(`/admin/email-template?kind=${kind}`);
    setTpl(data);
  }, []);

  const load = useCallback(async () => {
    try {
      const [s, m, aff, comm, dest] = await Promise.all([
        api.get("/admin/stats"),
        api.get("/admin/members"),
        api.get("/admin/affiliates"),
        api.get("/admin/commission"),
        api.get("/admin/link-destinations"),
      ]);
      setStats(s.data);
      setMembers(m.data);
      setAffiliates(aff.data);
      setCommission(comm.data);
      setDestinations(dest.data);
      await loadTemplate(tplKind);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to load");
    }
  }, [loadTemplate, tplKind]);

  useEffect(() => { load(); }, [load]);

  const fetchAwaiting = useCallback(() => {
    api.get("/admin/tickets/awaiting-count").then((r) => setAwaitingCount(r.data.count || 0)).catch(() => {});
  }, []);
  useEffect(() => { fetchAwaiting(); }, [fetchAwaiting, activeTab]);

  // Warn before leaving with unsaved edits
  useEffect(() => {
    const handler = (e) => { if (edit.open && dirty) { e.preventDefault(); e.returnValue = ""; } };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [edit.open, dirty]);

  const wrap = async (fn, okMsg) => {
    try { await fn(); if (okMsg) toast.success(okMsg); load(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const approve = (id) => wrap(() => api.post(`/admin/members/${id}/approve`), "Member approved & code issued");
  const setStatus = (id, status) => wrap(() => api.patch(`/admin/members/${id}`, { status }), `Member ${status}`);
  const deleteMember = (id) => wrap(() => api.delete(`/admin/members/${id}`), "Member removed");
  const resendMember = (id) => wrap(() => api.post(`/admin/members/${id}/send-welcome`), "Welcome email sent");

  const createMember = () => wrap(async () => {
    await api.post("/admin/members", memberForm);
    setMemberDialog(false);
    setMemberForm({ name: "", email: "", phone: "", password: "", generate_code: true, is_affiliate: false });
  }, "Member created");

  const openEdit = (m) => {
    const f = { name: m.name || "", email: m.email || "", phone: m.phone || "", status: m.status || "pending", is_affiliate: !!m.is_affiliate, access_code: m.access_code || "", code_active: !!m.code_active, commission_percent: m.commission_percent ?? "", promo_code: m.promo_code || "" };
    setEditForm(f); setEditOriginal(f); setEdit({ open: true, id: m.id });
  };

  const generateCodeInEdit = async () => {
    try {
      const { data } = await api.post(`/admin/members/${edit.id}/set-code`, {});
      setEditForm((f) => ({ ...f, access_code: data.access_code, code_active: true }));
      setEditOriginal((o) => ({ ...o, access_code: data.access_code, code_active: true }));
      toast.success("New code generated & welcome email sent");
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const saveEdit = async () => {
    setSaving(true);
    try {
      await api.patch(`/admin/members/${edit.id}`, {
        name: editForm.name, email: editForm.email, phone: editForm.phone,
        status: editForm.status, is_affiliate: editForm.is_affiliate,
        commission_percent: editForm.is_affiliate && editForm.commission_percent !== "" ? Number(editForm.commission_percent) : undefined,
        promo_code: editForm.is_affiliate ? (editForm.promo_code || "") : undefined,
      });
      if (editForm.access_code !== editOriginal.access_code && /^\d{10}$/.test(editForm.access_code)) {
        await api.post(`/admin/members/${edit.id}/set-code`, { code: editForm.access_code });
      }
      if (editForm.code_active !== editOriginal.code_active && editForm.access_code) {
        await api.patch(`/admin/members/${edit.id}/code-status`, { active: editForm.code_active });
      }
      toast.success("Changes saved");
      setEditOriginal(editForm);
      setEdit({ open: false, id: null });
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not save changes");
      // keep dialog open and edits intact
    } finally {
      setSaving(false);
    }
  };

  const closeEdit = (open) => {
    if (!open && dirty) { setDiscardOpen(true); return; }
    setEdit({ open, id: open ? edit.id : null });
  };

  const confirmDiscard = () => {
    setDiscardOpen(false);
    setEdit({ open: false, id: null });
  };

  const changeTplKind = async (kind) => {
    setTplKind(kind);
    try { await loadTemplate(kind); } catch (err) { toast.error("Failed to load template"); }
  };
  const saveTemplate = () => wrap(() => api.put("/admin/email-template", { subject: tpl.subject, body_html: tpl.body_html, kind: tplKind }), "Email template saved");
  const sendTestEmail = () => wrap(() => api.post("/admin/email-template/test", { recipient_email: testEmail, subject: tpl.subject, body_html: tpl.body_html, kind: tplKind }), `Test email sent to ${testEmail}`);

  const copyText = (text, label) => { navigator.clipboard.writeText(text).then(() => toast.success(`${label} copied`)).catch(() => toast.success(`${label} copied`)); };
  const saveCommission = () => wrap(() => api.put("/admin/commission", { percent: Number(commission.percent) || 0, membership_fee: Number(commission.membership_fee) || 0 }), "Commission settings saved");
  const openAffDetail = async (aff) => {
    try {
      const [r, p, l, n] = await Promise.all([
        api.get(`/admin/affiliates/${aff.id}/referrals`),
        api.get(`/admin/affiliates/${aff.id}/payments`),
        api.get(`/admin/affiliates/${aff.id}/links`),
        api.get(`/admin/affiliates/${aff.id}/notes`),
      ]);
      setAffDetail({ open: true, affiliate: aff, referrals: r.data, payments: p.data });
      setAffLinks(l.data); setAffNotes(n.data);
      setPaySel([]); setPayMeta({ method: "", reference: "" });
      setNewLink({ name: "", destination: "homepage", commission_percent: "", promo_code: "" }); setNewNote("");
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const refreshAffDetail = async () => {
    if (!affDetail.affiliate) return;
    const id = affDetail.affiliate.id;
    const [r, p, l, n] = await Promise.all([
      api.get(`/admin/affiliates/${id}/referrals`),
      api.get(`/admin/affiliates/${id}/payments`),
      api.get(`/admin/affiliates/${id}/links`),
      api.get(`/admin/affiliates/${id}/notes`),
    ]);
    setAffDetail((d) => ({ ...d, referrals: r.data, payments: p.data }));
    setAffLinks(l.data); setAffNotes(n.data);
  };

  const editAffiliate = (aff) => {
    const m = members.find((x) => x.id === aff.id) || aff;
    openEdit(m);
  };

  const togglePaySel = (id) => setPaySel((s) => s.includes(id) ? s.filter((x) => x !== id) : [...s, id]);
  const unpaidRefs = affDetail.referrals.filter((r) => r.status === "approved" && !r.commission_paid && r.commission_amount);
  const payTotal = affDetail.referrals.filter((r) => paySel.includes(r.id)).reduce((s, r) => s + Number(r.commission_amount || 0), 0);

  const recordPayment = () => wrap(async () => {
    await api.post(`/admin/affiliates/${affDetail.affiliate.id}/pay`, {
      referral_ids: paySel, method: payMeta.method || null, reference: payMeta.reference || null,
    });
    setPaySel([]); setPayMeta({ method: "", reference: "" });
    await refreshAffDetail(); load();
  }, "Payment recorded");

  const saveLink = () => wrap(async () => {
    const payload = {
      name: newLink.name,
      destination: newLink.destination,
      promo_code: newLink.promo_code,
      commission_percent: newLink.commission_percent === "" ? null : Number(newLink.commission_percent),
    };
    if (editingLink) {
      await api.patch(`/admin/affiliates/${affDetail.affiliate.id}/links/${editingLink}`, payload);
    } else {
      await api.post(`/admin/affiliates/${affDetail.affiliate.id}/links`, payload);
    }
    setNewLink({ name: "", destination: "homepage", commission_percent: "", promo_code: "" }); setEditingLink(null);
    await refreshAffDetail();
  }, editingLink ? "Link updated" : "Referral link created");

  const toggleLink = (l) => wrap(async () => {
    await api.patch(`/admin/affiliates/${affDetail.affiliate.id}/links/${l.id}`, { active: !l.active });
    await refreshAffDetail();
  }, "Link updated");

  const startEditLink = (l) => { setEditingLink(l.id); setNewLink({ name: l.name, destination: l.destination || "homepage", commission_percent: l.commission_percent ?? "", promo_code: l.promo_code || "" }); };
  const cancelEditLink = () => { setEditingLink(null); setNewLink({ name: "", destination: "homepage", commission_percent: "", promo_code: "" }); };

  const addNote = () => wrap(async () => {
    await api.post(`/admin/affiliates/${affDetail.affiliate.id}/notes`, { text: newNote });
    setNewNote("");
    await refreshAffDetail();
  }, "Note added");

  const revealPayment = (id) => api.post(`/admin/affiliates/${id}/reveal-payment`).then((r) => r.data);

  return (
    <div className="min-h-screen bg-[var(--ir-bg)] text-[var(--ir-text)]" data-testid="admin-dashboard">
      <Navbar />
      <main className="max-w-7xl mx-auto px-6 pt-40 pb-24">
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
          <p className="text-[var(--ir-orange)] tracking-[0.3em] uppercase text-xs font-semibold">Control Center</p>
          <h1 className="font-display text-4xl sm:text-5xl mt-3 text-[var(--ir-text)]">Admin Dashboard</h1>
        </motion.div>

        <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mt-10">
          <StatCard icon={Users} label="Total Members" value={stats?.total_members} accent="text-[var(--ir-blue)]" />
          <StatCard icon={Clock} label="Pending" value={stats?.pending} accent="text-[var(--ir-orange)]" />
          <StatCard icon={CheckCircle2} label="Approved" value={stats?.approved} accent="text-emerald-500" />
          <StatCard icon={Ban} label="Suspended" value={stats?.suspended} accent="text-rose-500" />
          <StatCard icon={Handshake} label="Affiliates" value={stats?.total_affiliates} accent="text-[var(--ir-blue)]" />
        </div>

        <Tabs value={activeTab} onValueChange={setTab} className="mt-12">
          <TabsList className="bg-white border border-[var(--ir-border)] rounded-none">
            <TabsTrigger value="members" className="rounded-none data-[state=active]:bg-[var(--ir-orange)] data-[state=active]:text-white" data-testid="tab-members">Members</TabsTrigger>
            <TabsTrigger value="affiliates" className="rounded-none data-[state=active]:bg-[var(--ir-orange)] data-[state=active]:text-white" data-testid="tab-affiliates">Affiliates</TabsTrigger>
            <TabsTrigger value="emails" className="rounded-none data-[state=active]:bg-[var(--ir-orange)] data-[state=active]:text-white" data-testid="tab-emails">Email Templates</TabsTrigger>
            <TabsTrigger value="marketing" className="rounded-none data-[state=active]:bg-[var(--ir-orange)] data-[state=active]:text-white" data-testid="tab-marketing">Marketing</TabsTrigger>
            <TabsTrigger value="announcements" className="rounded-none data-[state=active]:bg-[var(--ir-orange)] data-[state=active]:text-white" data-testid="tab-announcements">Announcements</TabsTrigger>
            <TabsTrigger value="support" className="rounded-none data-[state=active]:bg-[var(--ir-orange)] data-[state=active]:text-white" data-testid="tab-support">
              Support
              {awaitingCount > 0 && (
                <Badge className="ml-2 rounded-full bg-[var(--ir-blue)] text-white border-0 text-[10px] px-1.5 min-w-[18px] h-[18px] flex items-center justify-center" data-testid="support-awaiting-badge">{awaitingCount}</Badge>
              )}
            </TabsTrigger>
          </TabsList>

          <TabsContent value="members" className="mt-6">
            <MembersTab members={members} onAdd={() => setMemberDialog(true)} openEdit={openEdit} approve={approve} setStatus={setStatus} resendMember={resendMember} deleteMember={deleteMember} />
          </TabsContent>

          <TabsContent value="affiliates" className="mt-6">
            <AffiliatesTab commission={commission} setCommission={setCommission} saveCommission={saveCommission} affiliates={affiliates} editAffiliate={editAffiliate} openAffDetail={openAffDetail} copyText={copyText} />
          </TabsContent>

          <TabsContent value="emails" className="mt-6">
            <NotificationSettings />
            <EmailTemplatesTab tpl={tpl} setTpl={setTpl} tplKind={tplKind} changeTplKind={changeTplKind} saveTemplate={saveTemplate} testEmail={testEmail} setTestEmail={setTestEmail} sendTestEmail={sendTestEmail} />
          </TabsContent>

          <TabsContent value="marketing" className="mt-6">
            <AdminMarketing />
          </TabsContent>

          <TabsContent value="announcements" className="mt-6">
            <AdminAnnouncements />
          </TabsContent>

          <TabsContent value="support" className="mt-6">
            <AdminTickets onChange={fetchAwaiting} />
          </TabsContent>
        </Tabs>
      </main>

      <MemberFormDialog open={memberDialog} setOpen={setMemberDialog} memberForm={memberForm} setMemberForm={setMemberForm} createMember={createMember} />

      <EditMemberDialog
        edit={edit} closeEdit={closeEdit} editForm={editForm} setEditForm={setEditForm}
        dirty={dirty} saving={saving} saveEdit={saveEdit} generateCodeInEdit={generateCodeInEdit} commission={commission}
        discardOpen={discardOpen} setDiscardOpen={setDiscardOpen} confirmDiscard={confirmDiscard}
      />

      <AffiliateDetailDialog
        affDetail={affDetail} setAffDetail={setAffDetail} affLinks={affLinks} affNotes={affNotes}
        newLink={newLink} setNewLink={setNewLink} destinations={destinations} editingLink={editingLink}
        saveLink={saveLink} toggleLink={toggleLink} startEditLink={startEditLink} cancelEditLink={cancelEditLink}
        newNote={newNote} setNewNote={setNewNote} addNote={addNote}
        paySel={paySel} togglePaySel={togglePaySel} unpaidRefs={unpaidRefs} payTotal={payTotal}
        payMeta={payMeta} setPayMeta={setPayMeta} recordPayment={recordPayment} copyText={copyText}
        revealPayment={revealPayment}
      />
    </div>
  );
};

export default AdminDashboard;
